/**
 * The two behaviour choices the review step offers, mapped to the input fields
 * that store them — `firstPullFlag` and `postFlag`.
 *
 * `InputBehaviorComponent.jsx` stays the source of truth for which constant
 * means what; ADVANCED still renders it, and this file must not disagree with
 * it. What it adds is the design's shorter labels and its per-option helper
 * line: the Figma frame moves each label's qualifier down into the line beneath
 * it, so "The next new item published" becomes "Next new item" + "Waits for your
 * next new item, then posts it." That is strictly more than prod's label alone,
 * and three short labels fit the segmented toggle where three sentences did not.
 *
 * The same labels and helper lines also sit in `sampleData.ts` as
 * POST_FIRST_HELP / POST_WHEN_HELP. That duplication is on purpose: the harness
 * has no constants bag to key a value off, and it is the design kit's record of
 * the Figma copy while this file is prod's. If the copy changes, change both —
 * this one is the one users see.
 */

/**
 * `window.DlvrConstants`, as callers hold it (`app.config.constants`). Typed as
 * a bag because the generated constant file is not a module.
 */
type Constants = Record<string, number>

/** The input record's fields this file reads. Legacy data, so all optional. */
export type BehaviorInputRecord = {
  firstPullFlag?: number | string | null
  postFlag?: number | string | null
}

/**
 * An option plus the helper line that appears when it is the one selected.
 *
 * One list rather than an options array and a separate help table: the help is
 * per option, so keeping them together is what stops an option being added
 * without its line. Assignable to `ConfigureOption[]` as-is, so a caller hands
 * the same array to `ReviewStep` and reads `help` off it.
 */
export type BehaviorChoice = { value: string; label: string; help: string }

/** `data-field` suffixes, appended to the caller's statePath. */
export const REVIEW_BEHAVIOR_PATHS = {
  name: "name",
  firstPull: "firstPullFlag",
  postWhen: "postFlag",
} as const

/** "What should we post first?" — `input.firstPullFlag`. */
export function firstPullChoices(constants: Constants): BehaviorChoice[] {
  return [
    {
      value: String(constants.FIRSTPULL_SINGLE),
      label: "Most recent item",
      help: "Starts with your newest item and moves forward.",
    },
    {
      value: String(constants.FIRSTPULL_OFF),
      label: "Next new item",
      help: "Waits for your next new item, then posts it.",
    },
    {
      value: String(constants.FIRSTPULL_ON),
      label: "Most recent 10 items",
      help: "Posts your last 10 items now, then continues.",
    },
  ]
}

/** "When should we post new items?" — `input.postFlag`. */
export function postWhenChoices(constants: Constants): BehaviorChoice[] {
  return [
    {
      value: String(constants.POST_NOW),
      label: "Post immediately",
      help: "Posts new items as soon as we find them.",
    },
    {
      value: String(constants.POST_QUEUE),
      label: "Add to queue and post later",
      help: "Queues new items to post on your schedule.",
    },
  ]
}

/**
 * The stored value as a string, since that is what a toggle group and a select
 * both round-trip.
 *
 * The fallbacks are `InputDefaultsModule`'s, not the backend's: every input this
 * step sees came through `setInputDefaults`, which stamps FIRSTPULL_SINGLE and
 * POST_NOW. `??` rather than `||` because POST_NOW is 0 and would otherwise
 * fall through to itself by accident rather than by intent.
 */
export function behaviorValue(
  stored: number | string | null | undefined,
  fallback: number
): string {
  return String(stored ?? fallback)
}

/** The helper line for whichever choice is selected, or undefined for none. */
export function choiceHelp(
  choices: BehaviorChoice[],
  value: string
): string | undefined {
  return choices.find((choice) => choice.value === value)?.help
}
